import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuIcon from '@mui/icons-material/Menu';
import { alpha, Container, Grow, ListSubheader, useScrollTrigger, useTheme } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Toolbar from '@mui/material/Toolbar';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import logo from '../assets/logo/logo.png';
import LanguageSelector from '../components/common/LanguageSelector';
import { ThemeContext } from '../context/ThemeContext';

interface Props {
    /**
     * Injected by the documentation to work in an iframe.
     * You won't need it on your project.
     */
    window?: () => Window;
}

const drawerWidth = 240;
const navItems = ['Home', 'About', 'Contact'];

export default function DrawerAppBar(props: Props) {
    const { window } = props;
    const [mobileOpen, setMobileOpen] = React.useState(false);
    const [moreOpen, setMoreOpen] = React.useState(false);

    const { mode, toggleMode } = React.useContext(ThemeContext);

    const handleDrawerToggle = () => {
        setMobileOpen((prevState) => !prevState);
    };

    const { t } = useTranslation();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const theme = useTheme();

    const isRouteActive = (path: string) => {
        if (path === "/") return pathname === "/";
        return pathname === path || pathname.startsWith(`${path}/`);
    };

    const drawer = (
        <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
            <Box sx={{ py: 2 }}>
                <Box
                    component="img"
                    src={logo}
                    alt="Enduro logo"
                    sx={{
                        width: 220,
                        // height: 56,
                        objectFit: "contain",
                    }}
                />
            </Box>
            {/* <CardMedia
                id="card-media"
                component="img"
                image={`${getAssetsBaseUrl()}/images/logo/logo.jpg`}
                alt={"1"}
                sx={(theme) => ({
                    // height: 180,
                    width: "100%",
                    objectFit: "contain"
                    // borderRadius: 1,
                })}
            /> */}

            <Divider sx={{ mx: 2 }} />
            <List>
                <ListItem disablePadding>
                    <ListItemButton
                        selected={isRouteActive("/")}
                        sx={(theme) => ({
                            textAlign: 'start',
                            borderRadius: 3,
                            mx: 2,
                            my: 0,
                            px: 2,
                            "&.Mui-selected": {
                                backgroundColor: "rgb(173 45 45 / 42%)",
                                border: "1px solid rgb(173 45 45 / 80%)",
                                color: "#fff",
                            }
                        })}
                        onClick={() => navigate("/")}
                    >
                        <ListItemText primary={t("appBar.home")} />
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                    <ListItemButton
                        selected={isRouteActive("/tours")}
                        sx={(theme) => ({
                            textAlign: 'start',
                            borderRadius: 3,
                            mx: 2,
                            my: 0,
                            px: 2,
                            "&.Mui-selected": {
                                backgroundColor: "rgb(173 45 45 / 42%)",
                                border: "1px solid rgb(173 45 45 / 80%)",
                                color: "#fff",
                            }
                        })}
                        onClick={() => navigate("/tours?tourId=2")}
                    >
                        <ListItemText primary={t("appBar.offer")} />
                    </ListItemButton>
                </ListItem>
            </List>
            <Divider sx={{ mx: 2, my: 1 }} />
            <List>
                <ListSubheader sx={{ textAlign: "left", px: 3, color: "text.secondary", backgroundColor: "transparent" }}>
                    Više
                </ListSubheader>
                {/* Link "Što nudimo" (/offer) – zakomentirano
                <ListItem disablePadding>
                    <ListItemButton
                        selected={isRouteActive("/offer")}
                        sx={(theme) => ({
                            textAlign: 'start',
                            borderRadius: 3,
                            mx: 2,
                            my: 0,
                            px: 2,
                            "&.Mui-selected": {
                                backgroundColor: "rgb(173 45 45 / 42%)",
                                border: "1px solid rgb(173 45 45 / 80%)",
                                color: "#fff",
                            }
                        })}
                        onClick={() => navigate("/offer")}
                    >
                        <ListItemText primary={t("specificOffer.title")} />
                    </ListItemButton>
                </ListItem>
                */}
                <ListItem disablePadding>
                    <ListItemButton
                        selected={isRouteActive("/gallery")}
                        sx={(theme) => ({
                            textAlign: 'start',
                            borderRadius: 3,
                            mx: 2,
                            my: 0,
                            px: 2,
                            "&.Mui-selected": {
                                backgroundColor: "rgb(173 45 45 / 42%)",
                                border: "1px solid rgb(173 45 45 / 80%)",
                                color: "#fff",
                            }
                        })}
                        onClick={() => navigate("/gallery")}
                    >
                        <ListItemText primary={t("appBar.gallery")} />
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                    <ListItemButton
                        selected={isRouteActive("/equipment")}
                        sx={(theme) => ({
                            textAlign: 'start',
                            borderRadius: 3,
                            mx: 2,
                            my: 0,
                            px: 2,
                            "&.Mui-selected": {
                                backgroundColor: "rgb(173 45 45 / 42%)",
                                border: "1px solid rgb(173 45 45 / 80%)",
                                color: "#fff",
                            }
                        })}
                        onClick={() => navigate("/equipment")}
                    >
                        <ListItemText primary={t("appBar.equipment")} />
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                    <ListItemButton
                        selected={isRouteActive("/about-us")}
                        sx={(theme) => ({
                            textAlign: 'start',
                            borderRadius: 3,
                            mx: 2,
                            my: 0,
                            px: 2,
                            "&.Mui-selected": {
                                backgroundColor: "rgb(173 45 45 / 42%)",
                                border: "1px solid rgb(173 45 45 / 80%)",
                                color: "#fff",
                            }
                        })}
                        onClick={() => navigate("/about-us")}
                    >
                        <ListItemText primary={t("appBar.aboutUs")} />
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                    <ListItemButton
                        selected={isRouteActive("/our-team")}
                        sx={(theme) => ({
                            textAlign: 'start',
                            borderRadius: 3,
                            mx: 2,
                            my: 0,
                            px: 2,
                            "&.Mui-selected": {
                                backgroundColor: "rgb(173 45 45 / 42%)",
                                border: "1px solid rgb(173 45 45 / 80%)",
                                color: "#fff",
                            }
                        })}
                        onClick={() => navigate("/our-team")}
                    >
                        <ListItemText primary={t("appBar.staffMember")} />
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                    <ListItemButton
                        selected={isRouteActive("/hotel")}
                        sx={(theme) => ({
                            textAlign: 'start',
                            borderRadius: 3,
                            mx: 2,
                            my: 0,
                            px: 2,
                            "&.Mui-selected": {
                                backgroundColor: "rgb(173 45 45 / 42%)",
                                border: "1px solid rgb(173 45 45 / 80%)",
                                color: "#fff",
                            }
                        })}
                        onClick={() => navigate("/hotel")}
                    >
                        <ListItemText primary={t("specificOffer.hotel.title")} />
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                    <ListItemButton
                        selected={isRouteActive("/reviews")}
                        sx={(theme) => ({
                            textAlign: 'start',
                            borderRadius: 3,
                            mx: 2,
                            my: 0,
                            px: 2,
                            "&.Mui-selected": {
                                backgroundColor: "rgb(173 45 45 / 42%)",
                                border: "1px solid rgb(173 45 45 / 80%)",
                                color: "#fff",
                            }
                        })}
                        onClick={() => navigate("/reviews")}
                    >
                        <ListItemText primary={t("appBar.reviews")} />
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                    <ListItemButton
                        selected={isRouteActive("/terms-of-business")}
                        sx={(theme) => ({
                            textAlign: 'start',
                            borderRadius: 3,
                            mx: 2,
                            my: 0,
                            px: 2,
                            "&.Mui-selected": {
                                backgroundColor: "rgb(173 45 45 / 42%)",
                                border: "1px solid rgb(173 45 45 / 80%)",
                                color: "#fff",
                            }
                        })}
                        onClick={() => navigate("/terms-of-business")}
                    >
                        <ListItemText primary={t("appBar.termsOfBusiness")} />
                    </ListItemButton>
                </ListItem>
            </List>
        </Box>
    );

    const container = window !== undefined ? () => window().document.body : undefined;

    return (
        <>
            <CssBaseline />
            <ElevationScroll pathName={pathname}>
                <AppBar
                    component="nav"
                    sx={{
                        backgroundColor: alpha(theme.palette.background.paper, 0.75),
                        color: theme.palette.text.primary,
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                        backdropFilter: "blur(16px)",
                    }}
                >
                    <Container maxWidth='xl'>
                        <Toolbar sx={{ pl: { xs: 0, lg: 0 }, pr: { xs: 0, lg: 0 } }} variant='regular'>
                            <Box
                                display={"flex"}
                                flexDirection={"row"}
                                justifyContent={"flex-start"}
                                sx={{ flexGrow: 1, }}>
                                <Box
                                    onClick={() => navigate("/")}
                                    component="img"
                                    src={logo}
                                    alt="Enduro logo"
                                    sx={{
                                        width: { xs: 140, sm: 200 },
                                        // height: 40,
                                        borderRadius: 0.8,
                                        objectFit: "contain",
                                        cursor: "pointer"
                                    }}
                                />
                            </Box>

                            <Box display={"flex"} gap={1} flexDirection={"row"} alignItems={"center"} sx={{ display: { xs: 'flex', sm: 'none' } }} ml={1.5}>
                                {/* <IconButton onClick={toggleMode} color="inherit">
                                    {mode === 'light' ? <Brightness4Icon /> : <Brightness7Icon />}
                                </IconButton> */}
                                <LanguageSelector />
                            </Box>
                            <IconButton
                                color="inherit"
                                aria-label="open drawer"
                                edge="start"
                                onClick={handleDrawerToggle}
                                sx={{ ml: 1, display: { sm: 'none' } }}
                            >
                                <MenuIcon />
                            </IconButton>

                            <Box
                                sx={{
                                    display: { xs: 'none', sm: 'flex' },
                                    flexGrow: { xs: 0, md: 1 },
                                    mr: { xs: 0, md: 18 }
                                }}
                                gap={1}>
                                <Button
                                    sx={(theme) => ({
                                        px: 2,
                                        py: 1,
                                        borderRadius: 999,
                                        fontWeight: 600,
                                        fontSize: 16,
                                        textTransform: "none",
                                        color: isRouteActive("/") ? "#fff" : theme.palette.text.primary,
                                        backgroundColor: isRouteActive("/") ? "rgb(173 45 45 / 30%)" : "transparent",
                                        border: isRouteActive("/") ? "1px solid rgb(173 45 45 / 80%)" : "transparent",
                                        "&:hover": {
                                            backgroundColor: isRouteActive("/") ? "rgb(173 45 45 / 40%)" : "rgb(173 45 45 / 15%)",
                                        }
                                    })}
                                    onClick={() => navigate("/")}
                                >
                                    {t("appBar.home")}
                                </Button>
                                <Button
                                    sx={(theme) => ({
                                        px: 2,
                                        py: 1,
                                        borderRadius: 999,
                                        fontWeight: 600,
                                        fontSize: 16,
                                        textTransform: "none",
                                        color: isRouteActive("/tours") ? "#fff" : theme.palette.text.primary,
                                        backgroundColor: isRouteActive("/tours") ? "rgb(173 45 45 / 30%)" : "transparent",
                                        border: isRouteActive("/tours") ? "1px solid rgb(173 45 45 / 80%)" : "transparent",
                                        "&:hover": {
                                            backgroundColor: isRouteActive("/tours") ? "rgb(173 45 45 / 40%)" : "rgb(173 45 45 / 15%)",
                                        }
                                    })}
                                    onClick={() => navigate("/tours?tourId=2")}
                                >
                                    {t("appBar.offer")}
                                </Button>
                                <Box
                                    sx={{ position: "relative" }}
                                    onMouseEnter={() => setMoreOpen(true)}
                                    onMouseLeave={() => setMoreOpen(false)}
                                >
                                    <Button
                                        sx={(theme) => ({
                                            px: 2,
                                            py: 1,
                                            borderRadius: 999,
                                            fontWeight: 600,
                                            fontSize: 16,
                                            textTransform: "none",
                                            color: moreOpen ? "#fff" : theme.palette.text.primary,
                                            backgroundColor: moreOpen ? "rgb(173 45 45 / 30%)" : "transparent",
                                            border: moreOpen ? "1px solid rgb(173 45 45 / 80%)" : "transparent",
                                            "&:hover": {
                                                backgroundColor: moreOpen ? "rgb(173 45 45 / 40%)" : "rgb(173 45 45 / 15%)",
                                            }
                                        })}
                                    >
                                        Više{" "}
                                        <ExpandMoreIcon
                                            fontSize="small"
                                            sx={{
                                                transition: "transform 0.2s ease",
                                                transform: moreOpen ? "rotate(180deg)" : "rotate(0deg)",
                                            }}
                                        />
                                    </Button>
                                    <Grow in={moreOpen} timeout={160}>
                                        <Box
                                            sx={(theme) => ({
                                                position: "absolute",
                                                top: "100%",
                                                left: "50%",
                                                translate: "-50% 0",
                                                minWidth: 220,
                                                p: 1,
                                                borderRadius: 3,
                                                backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                                                boxShadow: `0 16px 40px ${alpha(theme.palette.common.black, 0.2)}`,
                                                zIndex: 10,
                                            })}
                                            onMouseEnter={() => setMoreOpen(true)}
                                            onMouseLeave={() => setMoreOpen(false)}
                                        >
                                            {[
                                                // { label: t("specificOffer.title"), path: "/offer" }, // "Što nudimo" – zakomentirano
                                                { label: t("appBar.gallery"), path: "/gallery" },
                                                { label: t("appBar.equipment"), path: "/equipment" },
                                                { label: t("appBar.aboutUs"), path: "/about-us" },
                                                { label: t("appBar.staffMember"), path: "/our-team" },
                                                { label: t("specificOffer.hotel.title"), path: "/hotel" },
                                                { label: t("appBar.reviews"), path: "/reviews" },
                                                { label: t("appBar.termsOfBusiness"), path: "/terms-of-business" },
                                            ].map((item) => (
                                                <Box
                                                    key={item.path}
                                                    onClick={() => navigate(item.path)}
                                                    sx={(theme) => ({
                                                        px: 2,
                                                        py: 1,
                                                        borderRadius: 2,
                                                        cursor: "pointer",
                                                        fontWeight: 600,
                                                        color: isRouteActive(item.path) ? "#fff" : theme.palette.text.primary,
                                                        backgroundColor: isRouteActive(item.path) ? "rgb(173 45 45 / 30%)" : "transparent",
                                                        "&:hover": {
                                                            backgroundColor: isRouteActive(item.path) ? "rgb(173 45 45 / 40%)" : "rgb(173 45 45 / 15%)",
                                                        }
                                                    })}
                                                >
                                                    {item.label}
                                                </Box>
                                            ))}
                                        </Box>
                                    </Grow>
                                </Box>
                            </Box>
                            <Box gap={1.5} display={"flex"} flexDirection={"row"} alignItems={"center"} sx={{ display: { xs: 'none', sm: 'flex' } }} ml={1.5}>
                                {/* <IconButton onClick={toggleMode} color="inherit">
                                    {mode === 'light' ? <Brightness4Icon /> : <Brightness7Icon />}
                                </IconButton> */}
                                <LanguageSelector />
                            </Box>
                        </Toolbar>
                    </Container>
                </AppBar>
            </ElevationScroll>
            <nav>
                <Drawer
                    anchor='right'
                    container={container}
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true, // Better open performance on mobile.
                    }}
                    sx={{
                        display: { xs: 'block', sm: 'none' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box', width: drawerWidth,
                            backgroundColor: "rgba(33, 33, 33, 0.7)",
                            backdropFilter: "blur(8px)"
                        },
                    }}
                >
                    {drawer}
                </Drawer>
            </nav>
            {/* <Box component="main">
                <Toolbar />
            </Box> */}
        </>

        // </Box>
    );
}

interface ElevationScrollProps {
    children: React.ReactElement;
    pathName: string;
}

function ElevationScroll(props: ElevationScrollProps) {
    const { children } = props;
    const theme = useTheme();
    // Note that you normally won't need to set the window ref as useScrollTrigger
    // will default to window.
    // This is only being set here because the demo is in an iframe.
    const trigger = useScrollTrigger({
        disableHysteresis: true,
        threshold: props.pathName === "/" ? 0 : 0,
    });

    // Na početnoj stranici, sakrij dok se ne skrola
    // if (props.pathName === "/" && !trigger) {
    //     return null;
    // }

    let style = {};

    // if (props.pathName === "/" || props.pathName === "/about-us") {
    //     style = {
    //         color: trigger ? "#fff" : "#fff",
    //         backgroundColor: trigger ? "#343434dd" : "#00000017",
    //         backdropFilter: trigger ? "blur(16px)" : "blur(8px)"
    //     }
    // }

    style = {
        color: theme.palette.text.primary,
        // backgroundColor: alpha(theme.palette.background.paper, trigger ? 0.85 : 0.6),
        backgroundColor: trigger ? "rgba(18, 18, 18, 0.8)" : "rgba(18, 18, 18, 0.8)",
        backdropFilter: trigger ? "blur(10px)" : "blur(10px)",
        transition: "all 0.3s ease-in-out",
    }

    return React.cloneElement(children, {
        elevation: trigger ? 1 : 0,
        style: style
    });
}